import { useCallback, useEffect, useState } from "react";
import { Card } from "@/src/components/wrappers/Card";
import { Button } from "@/src/components/ui/button";
import { fetch } from "@/src/lib/fetch";
import { cn } from "@/src/lib/utils";

/**
 * Communications (brief §9): HubSpot email engagements on the deal, payment
 * reminders sent from here, and queued system emails — with the actual message
 * viewable. Bodies load on demand and render in a script-less sandboxed iframe.
 */
type Row = {
  id: string;
  source: "hubspot" | "reminder" | "booking_reminder" | "scheduled" | "tracked";
  direction: "out" | "in";
  party: "client" | "operator" | "other";
  subject: string | null;
  at: string | null;
  from: string | null;
  to: string | null;
  status: string | null;
  hasBody: boolean;
  delivery: { state: string; at: string; detail: string | null } | null;
};
type State = { live: boolean; error?: string; rows: Row[]; counts: Record<string, number> };
const SOURCE: Record<Row["source"], string> = { hubspot: "HubSpot", reminder: "Reminder", booking_reminder: "Reminder", scheduled: "System", tracked: "Sent from here" };
// Delivery state folded from SendGrid events: green once the client has seen it, red when it never arrived.
const DELIVERY: Record<string, { label: string; cls: string }> = {
  sent: { label: "sent", cls: "bg-slate-100 text-slate-500" },
  deferred: { label: "delayed", cls: "bg-amber-50 text-amber-700" },
  delivered: { label: "delivered", cls: "bg-slate-100 text-slate-600" },
  opened: { label: "opened", cls: "bg-green-50 text-green-700" },
  clicked: { label: "link clicked", cls: "bg-green-50 text-green-700" },
  bounced: { label: "bounced", cls: "bg-red-50 text-red-700" },
  dropped: { label: "not sent", cls: "bg-red-50 text-red-700" },
  spam: { label: "marked spam", cls: "bg-red-50 text-red-700" },
};
const when = (s: string | null) => (s ? new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

export function CommsSection({ bookingId }: { bookingId: string }) {
  const [state, setState] = useState<State | null>(null);
  const [filter, setFilter] = useState<"all" | Row["party"]>("all");
  const [open, setOpen] = useState<{ row: Row; subject: string | null; html: string | null; text: string | null; events?: { event: string; at: string; detail: string | null }[] } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    fetch<{ data: State }>(`/api/bookings/${bookingId}/comms`).then((r: any) => setState(r.data)).catch((e: any) => setState({ live: false, error: e?.message, rows: [], counts: {} }));
  }, [bookingId]);
  useEffect(load, [load]);

  const view = async (row: Row) => {
    setBusy(row.id);
    try {
      const r: any = await fetch(`/api/bookings/${bookingId}/comms/${row.source}/${row.id}/body`);
      setOpen({ row, ...r.data });
    } finally {
      setBusy(null);
    }
  };

  if (!state) return null;
  const rows = state.rows.filter((r) => filter === "all" || r.party === filter);
  return (
    <Card className="flex w-full flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Communications</h3>
          <p className="text-xs text-slate-500">
            {state.rows.length} messages · HubSpot emails, reminders and everything sent from here, with delivery and open state where SendGrid reports it{state.live ? "" : " — HubSpot unreachable, showing local only"}
          </p>
        </div>
        <div className="flex gap-1">
          {(["all", "client", "operator", "other"] as const).map((f) => (
            <button key={f} type="button" onClick={() => setFilter(f)} className={cn("rounded-full px-3 py-1 text-xs font-medium", filter === f ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600")}>
              {f === "all" ? "All" : f[0].toUpperCase() + f.slice(1)}{f !== "all" && state.counts[f] ? ` ${state.counts[f]}` : ""}
            </button>
          ))}
        </div>
      </div>
      {state.error && <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">{state.error}</p>}
      {rows.length === 0 && <p className="text-sm text-slate-500">Nothing here yet.</p>}
      {rows.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {rows.map((r) => (
            <li key={`${r.source}:${r.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  <span className="mr-1 text-slate-400">{r.direction === "in" ? "←" : "→"}</span>
                  {r.subject ?? "(no subject)"}
                </p>
                <p className="truncate text-xs text-slate-500">
                  {when(r.at)} · {r.direction === "in" ? `from ${r.from ?? "?"}` : `to ${r.to ?? "?"}`} · {SOURCE[r.source]}{r.status ? ` · ${r.status.toLowerCase()}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {r.delivery && <span className={cn("rounded-full px-2 py-0.5 text-xs", DELIVERY[r.delivery.state]?.cls ?? "bg-slate-100 text-slate-500")} title={`${DELIVERY[r.delivery.state]?.label ?? r.delivery.state} · ${when(r.delivery.at)}${r.delivery.detail ? ` · ${r.delivery.detail}` : ""}`}>{DELIVERY[r.delivery.state]?.label ?? r.delivery.state}</span>}
                <span className={cn("rounded-full px-2 py-0.5 text-xs", r.party === "client" ? "bg-blue-50 text-blue-700" : r.party === "operator" ? "bg-purple-50 text-purple-700" : "bg-slate-100 text-slate-500")}>{r.party}</span>
                {r.hasBody && <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => view(r)}>{busy === r.id ? "…" : "View"}</Button>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {open && (
        <div className="rounded-lg border border-slate-200">
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
            <p className="truncate text-sm font-medium">{open.subject ?? open.row.subject ?? "(no subject)"}</p>
            <Button size="sm" variant="ghost" onClick={() => setOpen(null)}>Close</Button>
          </div>
          {open.events && open.events.length > 0 && (
            <p className="border-b border-slate-100 px-3 py-1.5 text-xs text-slate-500">
              {open.events.map((e) => `${DELIVERY[e.event === "open" ? "opened" : e.event === "click" ? "clicked" : e.event === "bounce" ? "bounced" : e.event === "processed" ? "sent" : e.event]?.label ?? e.event} ${when(e.at)}`).join(" → ")}
            </p>
          )}
          {open.html ? (
            <iframe title="email" sandbox="" srcDoc={open.html} className="h-[28rem] w-full bg-white" />
          ) : (
            <pre className="max-h-[28rem] overflow-auto whitespace-pre-wrap p-3 text-sm">{open.text ?? "No body stored for this message."}</pre>
          )}
        </div>
      )}
    </Card>
  );
}
